import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { useUpdateGeneralSettings } from '../../hooks/useUpdateGeneralSettings';
import { GeneralSettings } from '../../api/generated/model';
import {
  getSettingsKvKey,
  putSettingsKvKey,
  postSettingsKvKeyTest,
  getTimezones,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import Select from '../../components/common/Select';

const GeneralSettingsTab: React.FC = () => {
  // Fetch settings using React Query (with deduplication)
  const { data: fetchedGeneralSettings } = useGeneralSettings();

  // Fetch the IANA timezone list from the backend so the dropdown
  // matches what time.LoadLocation will actually accept on save.
  const { data: timezonesResp } = useQuery({
    queryKey: ['timezones'],
    queryFn: () => getTimezones(),
    staleTime: Infinity,
  });
  const timezoneOptions = useMemo(
    () => ((timezonesResp as any)?.data || []).map((tz: string) => ({ value: tz, label: tz })),
    [timezonesResp]
  );

  // Mutation hooks for updates
  const updateGeneralMutation = useUpdateGeneralSettings();

  // General settings state
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    id: 1,
    companyName: 'Company',
    commissionsConfig: null,
    businessTimezone: '',
  });
  const [originalSettings, setOriginalSettings] = useState<GeneralSettings>({
    id: 1,
    companyName: 'Company',
    commissionsConfig: null,
    businessTimezone: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Check if general settings have changed
  useEffect(() => {
    const changed = JSON.stringify(generalSettings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [generalSettings, originalSettings]);

  // Sync React Query data with local state when fetched
  useEffect(() => {
    if (fetchedGeneralSettings) {
      setGeneralSettings(fetchedGeneralSettings);
      setOriginalSettings(fetchedGeneralSettings);
    }
  }, [fetchedGeneralSettings]);

  // Handler for updating general settings
  const updateGeneralSetting = (field: string, value: any) => {
    setGeneralSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handler for canceling general settings changes
  const handleCancelGeneralSettings = () => {
    setGeneralSettings(originalSettings);
  };

  // Handler for saving general settings
  const handleSaveGeneralSettings = async () => {
    setIsSaving(true);
    setGeneralError(null);
    try {
      await updateGeneralMutation.mutateAsync({ data: generalSettings });
      // Update original settings to reflect saved state
      setOriginalSettings(generalSettings);
      console.log('General settings saved successfully');
    } catch (error: any) {
      console.error('Failed to save general settings:', error);
      // Extract error message from response
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save general settings';
      setGeneralError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="">
      <Card.Body>
        {generalError && (
          <Alert variant="danger" dismissible onClose={() => setGeneralError(null)}>
            {generalError}
          </Alert>
        )}
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  value={generalSettings.companyName}
                  onChange={(e) => updateGeneralSetting('companyName', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Business Timezone</Form.Label>
                <Select
                  options={timezoneOptions}
                  value={timezoneOptions.find((o: any) => o.value === generalSettings.businessTimezone) || null}
                  onChange={(opt) => updateGeneralSetting('businessTimezone', (opt as any)?.value || '')}
                  isClearable
                  placeholder="— select an IANA timezone —"
                  autoSize={false}
                />
                <Form.Text className="text-muted">
                  IANA timezone the business operates in. User-supplied date
                  filters are interpreted as calendar days in this zone so all
                  users see the same set of records for any given day. Required
                  for date-filtered queries.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Commissions Config <span className="text-muted">(JSON)</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={15}
                  placeholder='Paste CommissionsDB JSON configuration here'
                  value={generalSettings.commissionsConfig || ''}
                  onChange={(e) => updateGeneralSetting('commissionsConfig', e.target.value || null)}
                  style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                />
                <Form.Text className="text-muted">
                  JSON configuration that maps to the CommissionsDB structure. This will be used to configure commissions for sales people and sales leads.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* General Settings Actions */}
          <Row className="mt-4 pt-3 border-top">
            <Col className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                className="me-2"
                disabled={!hasChanges}
                onClick={handleCancelGeneralSettings}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!hasChanges || isSaving}
                onClick={handleSaveGeneralSettings}
              >
                {isSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

const SlackSettingsSection: React.FC = () => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger' | 'info'; text: string } | null>(null);

  useEffect(() => {
    loadWebhook();
  }, []);

  const loadWebhook = async () => {
    setIsLoading(true);
    try {
      const resp = await getSettingsKvKey('slack.payments_webhook');
      const value = (resp as any)?.value || '';
      setWebhookUrl(value);
      setOriginalUrl(value);
    } catch {
      // Setting doesn't exist yet
      setWebhookUrl('');
      setOriginalUrl('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      await putSettingsKvKey('slack.payments_webhook', { value: webhookUrl });
      setOriginalUrl(webhookUrl);
      setMessage({ type: 'success', text: 'Webhook URL saved.' });
    } catch {
      setMessage({ type: 'danger', text: 'Failed to save webhook URL.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setMessage(null);
    try {
      const resp = await postSettingsKvKeyTest('slack.payments_webhook');
      const result = resp as any;
      if (result?.success) {
        setMessage({ type: 'success', text: result.message || 'Test message sent!' });
      } else {
        setMessage({ type: 'danger', text: result?.message || 'Test failed.' });
      }
    } catch {
      setMessage({ type: 'danger', text: 'Failed to send test message.' });
    } finally {
      setIsTesting(false);
    }
  };

  const hasChanges = webhookUrl !== originalUrl;

  if (isLoading) {
    return (
      <Card className="mt-4">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <Card.Body>
        <h5 className="mb-3">
          <i className="bi bi-slack me-2"></i>
          Slack Integration
        </h5>
        <p className="text-muted mb-3">
          Configure a Slack incoming webhook to post check deposit notifications to the #payments channel.
        </p>

        {message && (
          <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Payments Channel Webhook URL</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://hooks.slack.com/services/..."
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
            />
            <Form.Text className="text-muted">
              Create an incoming webhook in your Slack workspace and paste the URL here.
            </Form.Text>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleTest}
              disabled={isTesting || !originalUrl}
            >
              {isTesting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Testing...
                </>
              ) : (
                <>
                  <i className="bi bi-send me-1"></i>
                  Test
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

const GeneralSettingsWithSlack: React.FC = () => (
  <>
    <GeneralSettingsTab />
    <SlackSettingsSection />
  </>
);

export default GeneralSettingsWithSlack;
